import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getOnboardingStatus } from "@/lib/services";

interface Props {
  children: JSX.Element;
}

/**
 * The member this tab has already seen finish onboarding. Onboarding can't be undone,
 * so moving between pages with their own guard (a post and the feed) renders at once
 * instead of going blank while the status is fetched again. Anyone else signing in
 * in this tab is checked afresh.
 */
let confirmedFor: string | null = null;

function signedInMember(): string | null {
  try {
    const stored = localStorage.getItem("user");
    return stored ? ((JSON.parse(stored) as { username?: string }).username ?? null) : null;
  } catch {
    return null;
  }
}

const alreadyConfirmed = () => confirmedFor !== null && confirmedFor === signedInMember();

export default function OnboardingRequiredRoute({ children }: Props) {
  const location = useLocation();
  const [loading, setLoading] = useState(() => !alreadyConfirmed());
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (alreadyConfirmed()) return;
    let mounted = true;
    (async () => {
      try {
        const member = signedInMember();
        const status = await getOnboardingStatus();
        if (status.onboarding_completed && member) confirmedFor = member;
        if (mounted) {
          setNeedsOnboarding(!status.onboarding_completed);
        }
      } catch {
        // If status fails, allow access to avoid lockout and rely on other guards
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null; // or a small loader UI
  if (needsOnboarding) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }
  return children;
}
